import moment from "moment";
import { useParams } from "react-router";
import { kFormatter } from "helpers/utility/formatters";
import { useDatasetVersion } from "helpers/utility/utilities";

import styles from "./style.module.scss";
import { useFeatureFlagService } from "../../../hooks/useFeatureFlagService";
import { DATASETS_JOB_SETUP_RECIPE_FILTERS } from "../../../helpers/constants/filters";
import classNames from "classnames";
import { useCamtekMetadata } from "./useCamtekMetadata";
import { INDEX_TYPES } from "helpers/constants/indexTypes";

interface DescriptionListProps {
  dataset: any;
  showJobSetupRecipe?: boolean;
  showDatasetInfo?: boolean;
  datasetLastUpdated?: string;
  className?: string;
  mode?: "vertical" | "horizontal";
}

const DescriptionListForDataPage = ({
  dataset,
  sourceName,
  imageCount,
  objectCount,
  videoCount,
  frameCount,
  sizeValue,
  createdAt,
  modelPack,
  datasetLastUpdated,
  creatorName,
  dsVersion,
  showJobSetupRecipe,
  showDatasetInfo,
  mode,
}: any) => {
  const datasetsJobSetupRecipeFiltersEnabled = useFeatureFlagService(
    DATASETS_JOB_SETUP_RECIPE_FILTERS,
  ).enabled;
  const { datasetId, clusterId, imageId } = useParams();

  const isImagePage = !!imageId && !!datasetId && !clusterId;

  const showSource = isImagePage;
  const showSize = !!sizeValue;
  const showCreatedAt = !!createdAt;
  const showDatasetLastUpdated = !!datasetLastUpdated;
  const showCreatedBy = !!creatorName;
  const showDsVersion = !!dsVersion;
  const showModelPack = !!modelPack;

  const { getCamtekMetadata, hasJobSetupRecipeMetadata } =
    useCamtekMetadata(dataset);

  const formatValue = (value: number) => {
    return kFormatter(value).toUpperCase();
  };

  const renderDescription = (title: string, description: string) => (
    <div className={styles.contentLeftBox}>
      <span className={styles.title}>{title}&nbsp;</span>
      <span className={styles.description}>{description}</span>
    </div>
  );

  const renderDescriptionBoxes = () => (
    <div
      className={classNames(styles.innerHeaderContentLeft, {
        [styles.vertical]: mode === "vertical",
      })}
    >
      {showDatasetInfo && (
        <>
          {showSource && renderDescription("Source", sourceName)}
          {!!imageCount && renderDescription("Images", formatValue(imageCount))}
          {!!objectCount &&
            renderDescription("Objects", formatValue(objectCount))}
          {!!videoCount && renderDescription("Videos", formatValue(videoCount))}
          {!!frameCount &&
            renderDescription("Video Frames", formatValue(frameCount))}
          {showSize && renderDescription("Size", sizeValue)}
          {showCreatedAt &&
            renderDescription(
              "Created",
              moment.utc(createdAt).local().fromNow(),
            )}
          {showDatasetLastUpdated &&
            renderDescription(
              "Last updated",
              moment.utc(datasetLastUpdated).local().fromNow(),
            )}
          {showCreatedBy && renderDescription("Created By", creatorName)}
          {showModelPack &&
            renderDescription(
              "Index Type",
              INDEX_TYPES.find((t) => t.id === modelPack)?.name ?? modelPack,
            )}
          {showDsVersion && renderDescription("Version", dsVersion)}
        </>
      )}
      {showJobSetupRecipe && datasetsJobSetupRecipeFiltersEnabled && (
        <>
          {showDatasetInfo && hasJobSetupRecipeMetadata && (
            <div className={styles.divider} />
          )}
          {getCamtekMetadata("Job Name") &&
            renderDescription("Job Name", getCamtekMetadata("Job Name"))}
          {getCamtekMetadata("Setup Name") &&
            renderDescription("Setup Name", getCamtekMetadata("Setup Name"))}
          {getCamtekMetadata("Recipes Name") &&
            renderDescription(
              "Recipes Name",
              getCamtekMetadata("Recipes Name"),
            )}
        </>
      )}
    </div>
  );

  return renderDescriptionBoxes();
};

const DescriptionList = ({
  dataset,
  showJobSetupRecipe = false,
  showDatasetInfo = true,
  datasetLastUpdated,
  className,
  mode = "horizontal",
}: DescriptionListProps) => {
  const dsVersion = useDatasetVersion(dataset?.pipeline_commit_id);
  return (
    <div className={styles.innerHeaderContent}>
      <DescriptionListForDataPage
        className={className}
        dataset={dataset}
        sourceName={dataset?.source_type}
        imageCount={dataset?.n_images}
        objectCount={dataset?.n_objects}
        videoCount={dataset?.n_videos}
        frameCount={dataset?.n_video_frames}
        sizeValue={dataset?.size_display_value}
        createdAt={dataset?.created_at}
        modelPack={dataset?.model_pack}
        datasetLastUpdated={datasetLastUpdated}
        creatorName={dataset?.creator_name}
        dsVersion={dsVersion}
        showJobSetupRecipe={showJobSetupRecipe}
        showDatasetInfo={showDatasetInfo}
        mode={mode}
      />
    </div>
  );
};

export default DescriptionList;
